import type { ExcludedAlertState } from '@kirin/api-client/contracts/alerts';

import { formatUtcDate, formatUtcDateTime } from '@/lib/date-utils';

/**
 * Build the three text rows shown in the excluded-alert tooltip from an
 * {@link ExcludedAlertState}. Returns `null` when the alert is not currently
 * excluded so callers can early-return instead of rendering the tooltip.
 *
 * Both date fields are rendered in **UTC** (see {@link formatUtcDateTime} /
 * {@link formatUtcDate} for the rationale). `excludedUntil === null` means
 * an indefinite exclusion and is surfaced as the literal string
 * `"Indefinite"` — the same vocabulary as `formatMutedTooltipLines` so users
 * do not have to learn two terms for the same "no expiry" semantic.
 *
 * The returned object is shape-compatible with `formatMutedTooltipLines`'s
 * output (three string keys: actor, when-applied, when-expires) so the two
 * tooltips can share rendering code if useful.
 */
export function formatExcludedTooltipLines(
  exclude: ExcludedAlertState | null | undefined
): { excluder: string; excludedAt: string; excludedUntil: string } | null {
  if (!exclude?.excluded) return null;
  const excluder =
    exclude.excludedByName ?? exclude.excludedByEmail ?? 'Unknown user';
  const excludedAt = formatUtcDateTime(exclude.excludedAt);
  const excludedUntil = exclude.excludedUntil
    ? formatUtcDate(exclude.excludedUntil)
    : 'Indefinite';
  return { excluder, excludedAt, excludedUntil };
}
